import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MoreVertical, Edit, Trash2, Eye, Search, X, Download, Lock, LockOpen } from 'lucide-react';
import VendorSearchModal from './VendorSearchModal';
import SearchableSelect from './SearchableSelect';
import ImageZoom from './ImageZoom';

// Searchable Select Component for GL Accounts
const SearchableGLAccountSelect = ({ value, onChange, glAccounts, defaultLabel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredAccounts = glAccounts.filter(account => {
    const search = searchTerm.toLowerCase();
    return (
      account.accountNumber.toLowerCase().includes(search) ||
      account.accountName.toLowerCase().includes(search)
    );
  });

  const selectedAccount = glAccounts.find(acc => acc.id === value);

  const handleSelect = (accountId) => {
    onChange(accountId);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left text-xs px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent min-w-[150px] bg-white flex items-center justify-between"
        title={selectedAccount ? `${selectedAccount.accountNumber} - ${selectedAccount.accountName}` : 'No GL Account'}
      >
        <span className="overflow-hidden text-ellipsis">
          {selectedAccount
            ? `${selectedAccount.accountNumber} - ${selectedAccount.accountName}`
            : 'No GL Account'}
        </span>
        <Search className="w-3 h-3 ml-1 flex-shrink-0 text-gray-400" />
      </button>

      {isOpen && (
        <div className="fixed z-[100] min-w-[400px] max-w-[600px] bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-hidden"
          style={{
            top: `${dropdownRef.current?.getBoundingClientRect().bottom}px`,
            left: `${dropdownRef.current?.getBoundingClientRect().left}px`
          }}
        >
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by code or name..."
                className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 pr-7"
                autoFocus
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          <div className="max-h-48 overflow-y-auto">
            <button
              onClick={() => handleSelect('')}
              className="w-full text-left px-3 py-2 text-xs hover:bg-gray-100 border-b border-gray-100"
            >
              <span className="text-gray-500 italic">No GL Account</span>
            </button>
            {filteredAccounts.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-500 italic">
                No accounts found
              </div>
            ) : (
              filteredAccounts.map(account => (
                <button
                  key={account.id}
                  onClick={() => handleSelect(account.id)}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-100 border-b border-gray-50 ${
                    value === account.id ? 'bg-primary-50 text-primary-700' : ''
                  }`}
                  title={`${account.accountNumber} - ${account.accountName}`}
                >
                  <div className="font-medium">{account.accountNumber}</div>
                  <div className="text-gray-600 break-words whitespace-normal">{account.accountName}</div>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {defaultLabel && (
        <div className="text-xs text-blue-500 mt-1">
          {defaultLabel}
        </div>
      )}
    </div>
  );
};

const TransactionManagement = ({ refreshTrigger, receiptIdFromUrl, onReceiptProcessed, isViewer }) => {
  const [transactions, setTransactions] = useState([]);
  const [userTransactions, setUserTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [vendors, setVendors] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [availableUserTransactions, setAvailableUserTransactions] = useState([]);
  const [matchModalSort, setMatchModalSort] = useState({ by: 'closestDate', dir: 'asc' });
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [selectedStatementTransaction, setSelectedStatementTransaction] = useState(null);
  const [showUserMatchModal, setShowUserMatchModal] = useState(false);
  const [selectedUserTransactionForMatch, setSelectedUserTransactionForMatch] = useState(null);
  const [availableStatementTransactions, setAvailableStatementTransactions] = useState([]);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedUserTransaction, setSelectedUserTransaction] = useState(null);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [statementGroups, setStatementGroups] = useState([]);
  const [activeView, setActiveView] = useState('groups'); // 'groups', 'transactions', 'user-transactions', 'suggested-matches', or 'deletion-logs'

  // Suggested matches state
  const [suggestedMatches, setSuggestedMatches] = useState([]);
  const [suggestedMatchCount, setSuggestedMatchCount] = useState(0);
  const [suggestedMatchesPagination, setSuggestedMatchesPagination] = useState({
    total: 0,
    page: 1,
    limit: 25,
    totalPages: 0
  });

  // Deletion logs state
  const [deletionLogs, setDeletionLogs] = useState([]);
  const [deletionLogsPagination, setDeletionLogsPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 0
  });

  // Create user transaction modal states
  const [showCreateTransactionModal, setShowCreateTransactionModal] = useState(false);
  const [createTransactionForm, setCreateTransactionForm] = useState({
    transactionDate: '',
    amount: '',
    vendor: '',
    category: '',
    notes: '',
    receiptImage: null
  });
  const [isCreatingTransaction, setIsCreatingTransaction] = useState(false);

  // Edit user transaction modal states
  const [showEditUserTransactionModal, setShowEditUserTransactionModal] = useState(false);
  const [editingUserTransaction, setEditingUserTransaction] = useState(null);
  const [editUserTransactionForm, setEditUserTransactionForm] = useState({
    vendor: '',
    notes: '',
    transactionDate: '',
    amount: ''
  });
  const [creditCards, setCreditCards] = useState([]);

  // Statement group editing states
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [openDropdownGroup, setOpenDropdownGroup] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [showEditStatementGroupModal, setShowEditStatementGroupModal] = useState(false);
  const [editingStatementGroup, setEditingStatementGroup] = useState(null);
  const [editStatementGroupForm, setEditStatementGroupForm] = useState({
    name: '',
    notes: ''
  });

  // Bulk selection for statement groups
  const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Vendor search modal states
  const [showVendorSearchModal, setShowVendorSearchModal] = useState(false);
  const [selectedTransactionForVendor, setSelectedTransactionForVendor] = useState(null);
  
  // Filtering states. Initial sortBy/sortOrder mirror the backend default (newest first)
  // so the active ▼ shows on the Date column when either table first loads.
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    cardNumber: '',
    statementGroupId: '',
    isMatched: '',
    vendorId: '',
    glAccountId: '',
    locationId: '',
    sortBy: 'date',
    sortOrder: 'desc',
    userId: '',
    amount: ''
  });
  
  // Pagination states
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
  });

  const [stats, setStats] = useState({
    totalTransactions: 0,
    totalAmount: 0,
    matchedCount: 0,
    unmatchedCount: 0,
    cardStats: []
  });

  useEffect(() => {
    if (activeView === 'groups') {
      fetchStatementGroups();
    } else if (activeView === 'transactions') {
      fetchTransactions();
    } else if (activeView === 'user-transactions') {
      fetchUserTransactions();
    } else if (activeView === 'suggested-matches') {
      fetchSuggestedMatches();
    } else if (activeView === 'deletion-logs') {
      fetchDeletionLogs();
    }
    // Clear bulk selection whenever we navigate away from groups or change the group list
    if (activeView !== 'groups') {
      setSelectedGroupIds(new Set());
    }
    fetchStats();
    fetchVendors();
    fetchGLAccounts();
    fetchCreditCards();
    fetchUsers();
    fetchLocations();
    fetchSuggestedMatchCount();
  }, [refreshTrigger, filters, pagination.page, activeView, deletionLogsPagination.page, suggestedMatchesPagination.page]);

  // Handle receipt deep link from URL
  useEffect(() => {
    console.log('receiptIdFromUrl changed:', receiptIdFromUrl);
    if (receiptIdFromUrl) {
      // Fetch the user transaction by ID and open the receipt modal
      const fetchAndOpenReceipt = async () => {
        try {
          console.log('Fetching receipt with ID:', receiptIdFromUrl);
          const response = await axios.get(`/api/user-transactions/${receiptIdFromUrl}`);
          console.log('Receipt fetched successfully:', response.data);
          setSelectedUserTransaction(response.data);
          setShowReceiptModal(true);
          setActiveView('transactions'); // Switch to transactions view

          // Clear the receipt ID after processing to allow future deep links
          if (onReceiptProcessed) {
            onReceiptProcessed();
          }
        } catch (error) {
          console.error('Error fetching receipt:', error);
          setError('Failed to load receipt');
          setTimeout(() => setError(''), 3000);

          // Clear the receipt ID even on error
          if (onReceiptProcessed) {
            onReceiptProcessed();
          }
        }
      };
      fetchAndOpenReceipt();
    }
  }, [receiptIdFromUrl, onReceiptProcessed]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId &&
          !event.target.closest('.relative') &&
          !event.target.closest('.dropdown-menu')) {
        console.log('Closing dropdown due to outside click');
        setOpenDropdownId(null);
        setOpenDropdownGroup(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);

  const fetchTransactions = async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });

      // Add includeArchived parameter
      if (filters.includeArchived) {
        params.set('includeArchived', 'true');
      }

      const response = await axios.get(`/api/statements/transactions?${params}`);
      setTransactions(response.data.transactions);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages
      }));
    } catch (error) {
      setError('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/statements/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchStatementGroups = async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit
      });

      // Add includeArchived filter
      if (filters.includeArchived) {
        params.append('includeArchived', 'true');
      }

      const response = await axios.get(`/api/statements/groups?${params}`);
      setStatementGroups(response.data.groups);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages
      }));
    } catch (error) {
      setError('Failed to fetch statement groups');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get('/api/vendors?limit=1000');
      setVendors(response.data.vendors || []);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    }
  };

  const fetchGLAccounts = async () => {
    try {
      const response = await axios.get('/api/gl-accounts?limit=1000');
      setGlAccounts(response.data.glAccounts || []);
    } catch (error) {
      console.error('Failed to fetch GL accounts:', error);
    }
  };

  const fetchCreditCards = async () => {
    try {
      const response = await axios.get('/api/credit-cards?limit=1000');
      setCreditCards(response.data.creditCards || []);
    } catch (error) {
      console.error('Failed to fetch credit cards:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/admin/users');
      // API returns users array directly, not wrapped in an object
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get('/api/locations');
      setLocations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const fetchUserTransactions = async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });

      const response = await axios.get(`/api/user-transactions?${params}`);
      setUserTransactions(response.data.transactions);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages
      }));
    } catch (error) {
      setError('Failed to fetch user transactions');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletionLogs = async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: deletionLogsPagination.page,
        limit: deletionLogsPagination.limit
      });

      const response = await axios.get(`/api/deletion-logs?${params}`);
      setDeletionLogs(response.data.logs);
      setDeletionLogsPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages
      }));
    } catch (error) {
      setError('Failed to fetch deletion logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestedMatches = async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: suggestedMatchesPagination.page,
        limit: suggestedMatchesPagination.limit
      });

      const response = await axios.get(`/api/user-transactions/suggested-matches?${params}`);
      setSuggestedMatches(response.data.suggestedMatches);
      setSuggestedMatchesPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages
      }));
    } catch (error) {
      console.error('Suggested matches error:', error.response?.status, error.response?.data || error.message);
      setError(`Failed to fetch suggested matches: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestedMatchCount = async () => {
    try {
      const response = await axios.get('/api/user-transactions/suggested-matches/count');
      setSuggestedMatchCount(response.data.count);
    } catch (error) {
      // Silently fail - badge just won't show
    }
  };

  const handleApproveSuggestion = async (suggestionId) => {
    try {
      await axios.post(`/api/user-transactions/suggested-matches/${suggestionId}/approve`);
      setSuccess('Suggested match approved and transactions linked');
      // Refresh all relevant data
      fetchSuggestedMatches();
      fetchSuggestedMatchCount();
      if (activeView === 'transactions') fetchTransactions();
      if (activeView === 'user-transactions') fetchUserTransactions();
      fetchStats();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to approve suggested match');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDismissSuggestion = async (suggestionId) => {
    try {
      await axios.post(`/api/user-transactions/suggested-matches/${suggestionId}/dismiss`);
      setSuccess('Suggested match dismissed');
      fetchSuggestedMatches();
      fetchSuggestedMatchCount();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to dismiss suggested match');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDeleteUserTransaction = async (transactionId, description) => {
    if (!window.confirm(`Are you sure you want to delete this user transaction?\n\n"${description}"`)) {
      return;
    }

    try {
      await axios.delete(`/api/user-transactions/${transactionId}`);
      setSuccess('User transaction deleted successfully');
      fetchUserTransactions();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to delete user transaction';
      const errorDetails = error.response?.data?.details;
      setError(errorDetails ? `${errorMessage}\n${errorDetails}` : errorMessage);
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleRestoreTransaction = async (logId) => {
    if (!window.confirm('Are you sure you want to restore this deleted transaction?')) {
      return;
    }

    try {
      const response = await axios.post(`/api/deletion-logs/${logId}/restore`);
      setSuccess(response.data.message || 'Transaction restored successfully');
      fetchDeletionLogs(); // Refresh deletion logs
      if (activeView === 'user-transactions') {
        fetchUserTransactions(); // Refresh user transactions if viewing that tab
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to restore transaction';
      const errorDetails = error.response?.data?.details;
      setError(errorDetails ? `${errorMessage}\n${errorDetails}` : errorMessage);
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleEditUserTransaction = (transaction) => {
    setEditingUserTransaction(transaction);
    setEditUserTransactionForm({
      vendor: transaction.vendor || '',
      notes: transaction.notes || '',
      transactionDate: transaction.transactionDate ? transaction.transactionDate.split('T')[0] : '',
      amount: transaction.amount || ''
    });
    setShowEditUserTransactionModal(true);
  };

  const handleUpdateUserTransaction = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.put(`/api/user-transactions/${editingUserTransaction.id}`, {
        vendor: editUserTransactionForm.vendor.trim(),
        notes: editUserTransactionForm.notes,
        transactionDate: editUserTransactionForm.transactionDate,
        amount: parseFloat(editUserTransactionForm.amount)
      });

      setSuccess('User transaction updated successfully');
      setShowEditUserTransactionModal(false);
      setEditingUserTransaction(null);
      fetchUserTransactions();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update user transaction');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveStatementGroup = async (groupId, groupName, shouldActivate) => {
    const action = shouldActivate ? 'unarchive' : 'archive';
    const confirmMessage = `Are you sure you want to ${action} the statement group "${groupName}"?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      await axios.put(`/api/statements/groups/${groupId}`, {
        isActive: shouldActivate
      });

      setSuccess(`Statement group "${groupName}" ${shouldActivate ? 'unarchived' : 'archived'} successfully`);

      // Refresh the statement groups list
      fetchStatementGroups();

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || `Failed to ${action} statement group`);
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProcessedStatus = async (groupId, groupName, currentStatus) => {
    const action = currentStatus ? 'unmark as processed' : 'mark as processed';
    const confirmMessage = currentStatus
      ? `Are you sure you want to unmark "${groupName}" as processed?\n\nThis will allow users to edit their matched transactions again.`
      : `Are you sure you want to mark "${groupName}" as processed?\n\nThis will prevent users from editing transactions matched to this group.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      await axios.put(`/api/statements/groups/${groupId}/processed`, {
        processed: !currentStatus
      });

      setSuccess(`Statement group "${groupName}" ${!currentStatus ? 'marked as processed' : 'unmarked as processed'} successfully`);

      // Close the dropdown
      setOpenDropdownId(null);
      setOpenDropdownGroup(null);

      // Refresh the statement groups list
      fetchStatementGroups();

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || `Failed to ${action} statement group`);
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStatementGroup = async (groupId, groupName, transactionCount) => {
    const confirmMessage = `Are you sure you want to delete the statement group "${groupName}"?\n\nThis will permanently delete:\n• The statement group\n• All ${transactionCount} transactions in this group\n• Any user transaction matches\n\nThis action cannot be undone.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.delete(`/api/statements/groups/${groupId}`);
      setSuccess(`Statement group "${groupName}" and ${response.data.deletedTransactionsCount} transactions deleted successfully`);

      // Refresh the statement groups list
      fetchStatementGroups();

      // Clear any filters that might reference this group
      if (filters.statementGroupId === groupId) {
        setFilters(prev => ({ ...prev, statementGroupId: '' }));
      }

      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete statement group');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const toggleGroupSelection = (groupId) => {
    setSelectedGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const toggleSelectAllGroups = () => {
    const allIds = statementGroups.map(g => g.id);
    const allSelected = allIds.length > 0 && allIds.every(id => selectedGroupIds.has(id));
    setSelectedGroupIds(allSelected ? new Set() : new Set(allIds));
  };

  const clearGroupSelection = () => setSelectedGroupIds(new Set());

  const runBulkGroupAction = async ({ ids, requestFn, actionLabel, pastTense }) => {
    if (ids.length === 0) return;

    const selectedGroups = statementGroups.filter(g => ids.includes(g.id));
    const namesPreview = selectedGroups.map(g => g.name).join('\n• ');
    const confirmMessage = actionLabel === 'delete'
      ? `Delete ${ids.length} statement group${ids.length === 1 ? '' : 's'}?\n\n• ${namesPreview}\n\nThis will permanently delete each group and all of its transactions, and unmatch any user transactions linked to them. This cannot be undone.`
      : `${actionLabel === 'mark as processed' ? 'Mark' : actionLabel === 'archive' ? 'Archive' : actionLabel} ${ids.length} statement group${ids.length === 1 ? '' : 's'}?\n\n• ${namesPreview}`;

    if (!window.confirm(confirmMessage)) return;

    setBulkActionLoading(true);
    setError('');
    try {
      const idToName = new Map(selectedGroups.map(g => [g.id, g.name]));
      const results = await Promise.allSettled(ids.map(id => requestFn(id)));
      const succeededIds = [];
      const failures = [];
      results.forEach((r, i) => {
        const id = ids[i];
        if (r.status === 'fulfilled') {
          succeededIds.push(id);
        } else {
          const msg = r.reason?.response?.data?.error || r.reason?.message || 'Unknown error';
          failures.push({ name: idToName.get(id) || id, msg });
        }
      });

      if (failures.length === 0) {
        setSuccess(`${succeededIds.length} statement group${succeededIds.length === 1 ? '' : 's'} ${pastTense}.`);
        setTimeout(() => setSuccess(''), 4000);
        clearGroupSelection();
      } else {
        const failedDetail = failures.map(f => `${f.name} — ${f.msg}`).join('; ');
        setError(`${succeededIds.length} ${pastTense}, ${failures.length} failed: ${failedDetail}`);
        setTimeout(() => setError(''), 8000);
        // Keep only failed ones selected so admin can retry
        setSelectedGroupIds(new Set(
          ids.filter((id, i) => results[i].status === 'rejected')
        ));
      }

      fetchStatementGroups();
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkGroupMarkProcessed = () => runBulkGroupAction({
    ids: Array.from(selectedGroupIds),
    requestFn: (id) => axios.put(`/api/statements/groups/${id}/processed`, { processed: true }),
    actionLabel: 'mark as processed',
    pastTense: 'marked as processed'
  });

  const handleBulkGroupArchive = () => runBulkGroupAction({
    ids: Array.from(selectedGroupIds),
    requestFn: (id) => axios.put(`/api/statements/groups/${id}`, { isActive: false }),
    actionLabel: 'archive',
    pastTense: 'archived'
  });

  const handleBulkGroupDelete = () => runBulkGroupAction({
    ids: Array.from(selectedGroupIds),
    requestFn: (id) => axios.delete(`/api/statements/groups/${id}`),
    actionLabel: 'delete',
    pastTense: 'deleted'
  });

  const handleEditStatementGroup = (group) => {
    setEditingStatementGroup(group);
    setEditStatementGroupForm({
      name: group.name,
      notes: group.notes || ''
    });
    setShowEditStatementGroupModal(true);
    setOpenDropdownId(null);
  };

  const handleEditStatementGroupFormChange = (field, value) => {
    setEditStatementGroupForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditStatementGroupSubmit = async (e) => {
    e.preventDefault();
    if (!editingStatementGroup) return;

    setLoading(true);
    try {
      await axios.put(`/api/statements/groups/${editingStatementGroup.id}`, {
        name: editStatementGroupForm.name,
        notes: editStatementGroupForm.notes
      });

      setSuccess(`Statement group "${editStatementGroupForm.name}" updated successfully`);
      setShowEditStatementGroupModal(false);
      setEditingStatementGroup(null);
      fetchStatementGroups();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update statement group');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseEditStatementGroup = () => {
    setShowEditStatementGroupModal(false);
    setEditingStatementGroup(null);
    setEditStatementGroupForm({ name: '', notes: '' });
  };

  const handleBulkAutoMatch = async () => {
    if (!window.confirm('This will automatically match all unmatched user transactions with statement transactions based on card number, date, and amount. Continue?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/user-transactions/bulk-auto-match');
      setSuccess(`${response.data.message} - ${response.data.totalMatched}/${response.data.totalProcessed} transactions matched.`);
      fetchUserTransactions();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to perform bulk auto-match');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleMatchTransaction = async (transactionId, statementTransactionId) => {
    try {
      await axios.post(`/api/user-transactions/${transactionId}/match`, {
        statementTransactionId
      });
      setSuccess('Transaction matched successfully');
      fetchUserTransactions();
      fetchSuggestedMatchCount();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to match transaction');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleUnmatchTransaction = async (transactionId) => {
    try {
      await axios.post(`/api/user-transactions/${transactionId}/unmatch`);
      setSuccess('Transaction unmatched successfully');
      fetchUserTransactions();
      fetchSuggestedMatchCount();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to unmatch transaction');
      setTimeout(() => setError(''), 3000);
    }
  };

  // State for suggested matches within match modals
  const [modalSuggestedMatches, setModalSuggestedMatches] = useState([]);
  const [modalSuggestedStmtMatches, setModalSuggestedStmtMatches] = useState([]);

  const handleStatementTransactionMatch = async (statementTransactionId) => {
    try {
      const response = await axios.get(`/api/statements/transactions/${statementTransactionId}/available-matches`);
      setSelectedStatementTransaction(response.data.statementTransaction);
      setAvailableUserTransactions(response.data.availableUserTransactions);
      setModalSuggestedMatches(response.data.suggestedMatches || []);
      setMatchModalSort({ by: 'closestDate', dir: 'asc' });
      setShowMatchModal(true);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to fetch available matches');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleStatementTransactionUnmatch = async (statementTransactionId) => {
    try {
      await axios.post(`/api/statements/transactions/${statementTransactionId}/unmatch`);
      setSuccess('Statement transaction unmatched successfully');
      fetchTransactions();
      fetchSuggestedMatchCount();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to unmatch statement transaction');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleUserTransactionMatch = async (userTransactionId) => {
    try {
      // Fetch available statement transactions for this user transaction
      const response = await axios.get(`/api/user-transactions/${userTransactionId}/potential-matches`);
      setSelectedUserTransactionForMatch(response.data.userTransaction);
      setAvailableStatementTransactions(response.data.potentialMatches || []);
      setModalSuggestedStmtMatches(response.data.suggestedMatches || []);
      setShowUserMatchModal(true);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to fetch available statement transactions');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleConfirmMatch = async (userTransactionId) => {
    try {
      await axios.post(`/api/statements/transactions/${selectedStatementTransaction.id}/match`, {
        userTransactionId
      });
      setSuccess('Transactions matched successfully');
      setShowMatchModal(false);
      setSelectedStatementTransaction(null);
      setAvailableUserTransactions([]);
      setModalSuggestedMatches([]);
      fetchTransactions();
      fetchSuggestedMatchCount();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to match transactions');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleConfirmUserMatch = async (statementTransactionId) => {
    try {
      await axios.post(`/api/user-transactions/${selectedUserTransactionForMatch.id}/match`, {
        statementTransactionId
      });
      setSuccess('Transactions matched successfully');
      setShowUserMatchModal(false);
      setSelectedUserTransactionForMatch(null);
      setAvailableStatementTransactions([]);
      setModalSuggestedStmtMatches([]);
      // Refresh user transactions to show updated match status
      fetchUserTransactions();
      fetchSuggestedMatchCount();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to match transactions');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleOpenCreateTransaction = () => {
    setError(''); // clear any stale error so the in-modal banner reflects only this attempt
    setShowCreateTransactionModal(true);
    // Pre-populate form with statement transaction data
    setCreateTransactionForm({
      transactionDate: selectedStatementTransaction?.transactionDate || '',
      amount: selectedStatementTransaction?.amount ? Math.abs(parseFloat(selectedStatementTransaction.amount)) : '',
      vendor: selectedStatementTransaction?.description || '',
      category: '',
      notes: '',
      receiptImage: null
    });
  };

  const handleCloseCreateTransaction = () => {
    setError(''); // clear the error shown in the modal so it doesn't linger on the page
    setShowCreateTransactionModal(false);
    setCreateTransactionForm({
      transactionDate: '',
      amount: '',
      vendor: '',
      category: '',
      notes: '',
      receiptImage: null
    });
  };

  const handleCreateTransactionFormChange = (field, value) => {
    setCreateTransactionForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCreateTransactionForm(prev => ({
        ...prev,
        receiptImage: file
      }));
    }
  };

  const handleCreateTransactionSubmit = async (e) => {
    e.preventDefault();
    setIsCreatingTransaction(true);
    
    try {
      // Find the user associated with this statement transaction's card number
      // We need to get users who have this card number
      // First try to get all users, then filter them client-side using the same logic as the matching
      const usersResponse = await axios.get(`/api/admin/users`);
      const allUsers = usersResponse.data;
      
      // Filter users to match the statement transaction's card number using flexible matching.
      // Users can have multiple cards via the many-to-many UserCreditCard join table, so we
      // check both the legacy primary card relation and the full creditCards[] array.
      const usersWithCard = allUsers.filter(user => {
        const statementCardNumber = selectedStatementTransaction.cardNumber;

        if (!statementCardNumber) return false;

        // Check direct cardNumber match
        if (user.cardNumber === statementCardNumber) return true;

        // Check if user cardNumber ends with statement cardNumber (last 4 digits)
        if (user.cardNumber && user.cardNumber.endsWith(statementCardNumber)) return true;

        // Check the denormalized lastFourCC field on User (kept in sync with the primary card)
        if (user.lastFourCC === statementCardNumber) return true;

        // Check legacy primary credit card lastFourCC match
        if (user.creditCard && user.creditCard.lastFourCC === statementCardNumber) return true;

        // Check all assigned credit cards (many-to-many) — handles users with multiple cards
        if (Array.isArray(user.creditCards) && user.creditCards.some(cc => cc.lastFourCC === statementCardNumber)) return true;

        return false;
      });
      
      if (usersWithCard.length === 0) {
        setError(`No user found with card number ending in ${selectedStatementTransaction.cardNumber}`);
        return;
      }
      
      // When multiple users have the same card (e.g. a shared corporate card), prefer the user
      // whose primary card matches; otherwise fall back to the first match.
      const statementCardNumber = selectedStatementTransaction.cardNumber;
      const targetUser =
        usersWithCard.find(u => u.lastFourCC === statementCardNumber) ||
        usersWithCard.find(u => u.creditCard && u.creditCard.lastFourCC === statementCardNumber) ||
        usersWithCard[0];
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('userId', targetUser.id);
      formData.append('transactionDate', createTransactionForm.transactionDate);
      formData.append('amount', createTransactionForm.amount);
      formData.append('vendor', createTransactionForm.vendor);
      if (createTransactionForm.category) formData.append('category', createTransactionForm.category);
      if (createTransactionForm.notes) formData.append('notes', createTransactionForm.notes);
      if (createTransactionForm.receiptImage) formData.append('receiptImage', createTransactionForm.receiptImage);
      
      // Create the user transaction
      const response = await axios.post('/api/admin/user-transactions', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const newUserTransaction = response.data.transaction;
      
      // Automatically match it to the statement transaction
      await axios.post(`/api/statements/transactions/${selectedStatementTransaction.id}/match`, {
        userTransactionId: newUserTransaction.id
      });
      
      setSuccess('User transaction created and matched successfully');
      setShowCreateTransactionModal(false);
      setShowMatchModal(false);
      setSelectedStatementTransaction(null);
      setAvailableUserTransactions([]);
      handleCloseCreateTransaction();
      fetchTransactions();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to create and match transaction');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsCreatingTransaction(false);
    }
  };

  const handleOpenReceipt = (userTransaction) => {
    setSelectedUserTransaction(userTransaction);
    setShowReceiptModal(true);
  };

  const handleCloseReceipt = () => {
    setShowReceiptModal(false);
    setSelectedUserTransaction(null);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when filtering
  };

  // Toggle a sort field. Click once = ASC, click same field again = DESC, click again = ASC.
  // Always resets pagination since sort changes which rows fall on page 1.
  const toggleSort = (field) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc'
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Render a sortable <th>. `field` is the backend sort key; `align` defaults to left.
  // `padding` lets callers match neighboring non-sortable cells (default px-4 py-3 matches
  // the User Transactions table; the denser All Transactions table passes 'px-2 py-2').
  // The arrow indicator is ALWAYS visible: faint ⇅ when this column isn't the active sort,
  // a solid ▲/▼ when it is, so users see at a glance which columns are sortable.
  const sortableTh = (label, field, align = 'left', padding = 'px-4 py-3') => {
    const active = filters.sortBy === field;
    const justify = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
    const arrowChar = active ? (filters.sortOrder === 'asc' ? '▲' : '▼') : '⇅';
    const arrowClass = active ? 'text-gray-900' : 'text-gray-400';
    return (
      <th className={`${padding} text-${align} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
        <button
          type="button"
          onClick={() => toggleSort(field)}
          className={`inline-flex items-center w-full whitespace-nowrap ${justify} hover:text-gray-700 ${active ? 'text-gray-900' : ''}`}
        >
          <span className="whitespace-nowrap">{label}</span>
          <span className={`ml-1 text-[10px] leading-none ${arrowClass}`}>{arrowChar}</span>
        </button>
      </th>
    );
  };

  const handleDeleteTransaction = async (transactionId, description) => {
    if (!window.confirm(`Are you sure you want to delete this transaction?\n\n"${description}"`)) {
      return;
    }

    try {
      await axios.delete(`/api/statements/transactions/${transactionId}`);
      setSuccess('Transaction deleted successfully');
      fetchTransactions();
      fetchStats();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to delete transaction');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleVendorChange = async (transactionId, vendorId) => {
    try {
      const response = await axios.put(`/api/statements/transactions/${transactionId}`, {
        vendorId: vendorId || null
      });

      // Update the transaction in the local state without refetching
      setTransactions(prevTransactions =>
        prevTransactions.map(t =>
          t.id === transactionId ? response.data : t
        )
      );

      setSuccess('Vendor assignment updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to update vendor assignment');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleOpenVendorSearchModal = (transaction) => {
    setSelectedTransactionForVendor(transaction);
    setShowVendorSearchModal(true);
  };

  const handleVendorSelected = (vendor) => {
    // Update the transaction in local state
    setTransactions(prevTransactions =>
      prevTransactions.map(t =>
        t.id === selectedTransactionForVendor.id
          ? { ...t, vendorId: vendor.id, vendor }
          : t
      )
    );

    setShowVendorSearchModal(false);
    setSelectedTransactionForVendor(null);
    setSuccess('Vendor assigned successfully');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleGLAccountChange = async (transactionId, glAccountId) => {
    try {
      const response = await axios.put(`/api/statements/transactions/${transactionId}`, {
        glAccountId: glAccountId || null
      });

      // Update the transaction in the local state without refetching
      setTransactions(prevTransactions =>
        prevTransactions.map(t =>
          t.id === transactionId ? response.data : t
        )
      );

      setSuccess('GL account updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to update GL account');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleLocationChange = async (transactionId, locationId) => {
    try {
      const response = await axios.put(`/api/statements/transactions/${transactionId}`, {
        locationId: locationId || null
      });

      // Update the transaction in the local state without refetching
      setTransactions(prevTransactions =>
        prevTransactions.map(t =>
          t.id === transactionId ? response.data : t
        )
      );

      setSuccess('Location updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to update location');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleSelectTransaction = (transactionId) => {
    setSelectedTransactions(prev => 
      prev.includes(transactionId)
        ? prev.filter(id => id !== transactionId)
        : [...prev, transactionId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTransactions.length === transactions.length) {
      setSelectedTransactions([]);
    } else {
      setSelectedTransactions(transactions.map(t => t.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTransactions.length === 0) {
      setError('No transactions selected');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedTransactions.length} selected transaction${selectedTransactions.length > 1 ? 's' : ''}?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // Delete transactions in parallel
      await Promise.all(
        selectedTransactions.map(id => 
          axios.delete(`/api/statements/transactions/${id}`)
        )
      );
      
      setSuccess(`Successfully deleted ${selectedTransactions.length} transaction${selectedTransactions.length > 1 ? 's' : ''}`);
      setSelectedTransactions([]);
      fetchTransactions();
      fetchStats();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to delete some transactions');
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatCurrency = (amount, type = null) => {
    const value = type?.trim() === 'Return' ? -Math.abs(amount) : Math.abs(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Handle both date-only strings and datetime strings
    // Split by 'T' first to remove any time component, then by space to handle timestamps
    const datePart = dateString.split('T')[0].split(' ')[0];
    const [year, month, day] = datePart.split('-');
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatStatementMonth = (monthString) => {
    if (!monthString) return '-';
    // Convert YYYY-MM format to "Month YYYY" format
    const [year, month] = monthString.split('-');
    if (!year || !month) return monthString;

    const date = new Date(year, parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      cardNumber: '',
      statementGroupId: '',
      isMatched: '',
      vendorId: '',
      glAccountId: '',
      locationId: '',
      sortBy: 'date',
      sortOrder: 'desc',
      amount: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const goToPage = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleExportR365 = async () => {
    try {
      setLoading(true);

      // Fetch all transactions with the current filters (no pagination limit)
      const params = new URLSearchParams({
        ...filters,
        limit: 10000 // Get all transactions
      });

      const response = await axios.get(`/api/statements/transactions?${params}`);
      const allTransactions = response.data.transactions;

      if (allTransactions.length === 0) {
        setError('No transactions to export');
        setTimeout(() => setError(''), 3000);
        return;
      }

      // Filter out Payment types from export
      const transactionsToExport = allTransactions.filter(transaction =>
        transaction.type?.trim() !== 'Payment'
      );

      if (transactionsToExport.length === 0) {
        setError('No transactions to export after filtering');
        setTimeout(() => setError(''), 3000);
        return;
      }

      // Generate CSV content
      const headers = [
        'Type',
        'Checking Account',
        'Number',
        'Paid To',
        'Check Memo',
        'Date',
        'Location',
        'Amount',
        'Address 1',
        'Address 2',
        'City',
        'State',
        'Zip',
        'TDAccount',
        'TDAmount',
        'TDComment',
        'TDLocation',
        'Vendor Name'
      ];

      const rows = transactionsToExport.map(transaction => {
        // Get master card checking account from credit card (it's stored as a string in MasterCard.glAccount)
        // Check multiple locations: userTransaction.creditCard (new direct), userTransaction.user.creditCard (legacy), or statement's creditCard lookup
        const checkingAccount =
          transaction.userTransaction?.creditCard?.masterCard?.glAccount ||
          transaction.userTransaction?.user?.creditCard?.masterCard?.glAccount ||
          transaction.creditCard?.masterCard?.glAccount ||
          '';

        // Get last 4 digits of master card
        const last4 =
          transaction.userTransaction?.creditCard?.masterCard?.lastFourDigits ||
          transaction.userTransaction?.user?.creditCard?.masterCard?.lastFourDigits ||
          transaction.creditCard?.masterCard?.lastFourDigits ||
          transaction.cardNumber ||
          '';

        // Format date as MM/DD/YYYY
        const transactionDate = transaction.transactionDate ? new Date(transaction.transactionDate + 'T00:00:00') : new Date();
        const dateStr = `${String(transactionDate.getMonth() + 1).padStart(2, '0')}/${String(transactionDate.getDate()).padStart(2, '0')}/${transactionDate.getFullYear()}`;

        // Generate number field: CC_Chase_XXXX_MM/DD/YYYY
        const number = `CC_Chase_${last4}_${dateStr}`;

        // Get receipt link - link to admin panel that shows receipt modal with all details
        let receiptLink = '';
        if (transaction.userTransaction?.id) {
          // Link to admin panel with transaction ID to open receipt modal
          // This shows the receipt image plus all transaction details (notes, vendor, category, etc.)
          receiptLink = `${window.location.origin}/receipt/${transaction.userTransaction.id}`;
        }

        // Get location number - prioritize stored location, then fallback to credit card location
        const locationNumber =
          transaction.location?.locationNumber ||
          transaction.userTransaction?.creditCard?.location?.locationNumber ||
          transaction.userTransaction?.user?.creditCard?.location?.locationNumber ||
          transaction.creditCard?.location?.locationNumber ||
          '';

        // Get GL Account number
        const glAccountNumber = transaction.glAccount?.accountNumber ||
                                transaction.vendor?.glAccount?.accountNumber || '';

        // Get vendor name
        const vendorName = transaction.vendor?.name || '';

        // Get amount - negative for returns, positive for sales
        const amount = transaction.amount ?
          (transaction.type?.trim() === 'Return' ? -Math.abs(parseFloat(transaction.amount)) : Math.abs(parseFloat(transaction.amount))) : '';

        return [
          'Bank Expense', // Type - always "Bank Expense" for sale transactions
          checkingAccount, // Checking Account - GL Account of Master Card (string field)
          number, // Number - CC_Chase_XXXX_MM/DD/YYYY
          transaction.description || '', // Paid To - transaction description
          receiptLink, // Check Memo - public link to receipt
          dateStr, // Date - transaction date
          locationNumber, // Location - location number from credit card
          amount, // Amount - negative for returns, positive for sales
          '', // Address 1 - blank
          '', // Address 2 - blank
          '', // City - blank
          '', // State - blank
          '', // Zip - blank
          glAccountNumber, // TDAccount - GL Code number
          amount, // TDAmount - negative for returns, positive for sales
          '', // TDComment - blank
          locationNumber, // TDLocation - location number from credit card
          vendorName // Vendor Name
        ];
      });

      // Convert to CSV
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          // Escape cells that contain commas, quotes, or newlines
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `R365_Upload_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccess(`Successfully exported ${transactionsToExport.length} transactions`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export transactions');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const renderStatementGroups = () => {
    const allVisibleIds = statementGroups.map(g => g.id);
    const allSelected = allVisibleIds.length > 0 && allVisibleIds.every(id => selectedGroupIds.has(id));
    const someSelected = allVisibleIds.some(id => selectedGroupIds.has(id));
    const selectionCount = selectedGroupIds.size;

    return (
    <div className="bg-white rounded-xl shadow-soft overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 border-b border-gray-200">
        <h3 className="text-xl font-semibold text-gray-900 mb-4 sm:mb-0">Statement Groups ({pagination.total} total)</h3>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="includeArchived"
            checked={filters.includeArchived || false}
            onChange={(e) => {
              setFilters(prev => ({ ...prev, includeArchived: e.target.checked }));
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
          />
          <label htmlFor="includeArchived" className="text-sm text-gray-700 cursor-pointer">
            Include Archived
          </label>
        </div>
      </div>

      {!isViewer && selectionCount > 0 && (
        <div className="flex flex-wrap items-center gap-3 px-6 py-3 bg-blue-50 border-b border-blue-100">
          <span className="text-sm font-medium text-blue-900">
            {selectionCount} selected
          </span>
          <button
            onClick={handleBulkGroupMarkProcessed}
            disabled={bulkActionLoading}
            className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors duration-200 disabled:opacity-50"
          >
            Mark Processed
          </button>
          <button
            onClick={handleBulkGroupArchive}
            disabled={bulkActionLoading}
            className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded-lg transition-colors duration-200 disabled:opacity-50"
          >
            Archive
          </button>
          <button
            onClick={handleBulkGroupDelete}
            disabled={bulkActionLoading}
            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg transition-colors duration-200 disabled:opacity-50"
          >
            Delete
          </button>
          <button
            onClick={clearGroupSelection}
            disabled={bulkActionLoading}
            className="px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs font-medium rounded-lg transition-colors duration-200 disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="text-gray-500">Loading statement groups...</div>
        </div>
      ) : statementGroups.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No statement groups found. Upload a CSV file to get started.</p>
        </div>
      ) : (
        <div>
          <div className="overflow-x-auto relative">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {!isViewer && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                      <input
                        type="checkbox"
                        aria-label="Select all statement groups"
                        ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                        checked={allSelected}
                        onChange={toggleSelectAllGroups}
                        className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
                      />
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statement Month</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Upload Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Amount</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Transactions</th>
                  {!isViewer && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Matched</th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {statementGroups.map(group => (
                  <tr key={group.id} className={`hover:bg-gray-50 transition-colors duration-150 ${selectedGroupIds.has(group.id) ? 'bg-blue-50' : ''}`}>
                    {!isViewer && (
                      <td className="px-4 py-4 text-center">
                        <input
                          type="checkbox"
                          aria-label={`Select ${group.name}`}
                          checked={selectedGroupIds.has(group.id)}
                          onChange={() => toggleGroupSelection(group.id)}
                          className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        {group.processed ? (
                          <Lock className="w-4 h-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <LockOpen className="w-4 h-4 text-red-600 flex-shrink-0" />
                        )}
                        <div className="text-sm font-medium text-gray-900">{group.name}</div>
                        {!group.isActive && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                            Archived
                          </span>
                        )}
                        {group.processed && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            Processed
                          </span>
                        )}
                      </div>
                      {group.notes && (
                        <div className="text-xs text-gray-500 mt-1">{group.notes}</div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {formatStatementMonth(group.statementMonth)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">{formatDateTime(group.uploadedAt)}</td>
                    <td className="px-4 py-4 text-right text-sm font-semibold text-gray-900">
                      {formatCurrency(group.totalAmount)}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {group.totalTransactions}
                      </span>
                    </td>
                    {!isViewer && (
                      <td className="px-4 py-4 text-center">
                        <div className="flex flex-col items-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {group.userMatchedTransactions || 0}
                          </span>
                          <div className="text-xs text-gray-500 mt-1">
                            {group.totalTransactions > 0
                              ? Math.round(((group.userMatchedTransactions || 0) / group.totalTransactions) * 100)
                              : 0}%
                          </div>
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors duration-200"
                          onClick={() => {
                            setFilters(prev => ({ ...prev, statementGroupId: group.id }));
                            setActiveView('transactions');
                            setPagination(prev => ({ ...prev, page: 1 }));
                          }}
                        >
                          <Eye className="w-3 h-3 mr-1 inline" />
                          View Transactions
                        </button>

                        {/* Three-dot menu */}
                        {!isViewer && (
                          <div className="relative">
                            <button
                              className="p-1 rounded-md hover:bg-gray-100 transition-colors duration-200"
                              onClick={(e) => {
                                console.log('Three-dot button clicked for group:', group);
                                if (openDropdownId === group.id) {
                                  setOpenDropdownId(null);
                                  setOpenDropdownGroup(null);
                                } else {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setDropdownPosition({
                                    top: rect.bottom,
                                    left: rect.left - 180 // 192px width - 12px padding
                                  });
                                  setOpenDropdownId(group.id);
                                  setOpenDropdownGroup(group);
                                  console.log('Set dropdown group to:', group);
                                }
                              }}
                            >
                              <MoreVertical className="w-4 h-4 text-gray-600" />
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center px-6 py-4 bg-gray-50 border-t border-gray-200 space-x-4">
              <button 
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200" 
                onClick={() => goToPage(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
              
              <span className="text-sm text-gray-600">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              
              <button 
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200" 
                onClick={() => goToPage(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
    );
  };

  const renderUserTransactions = () => (
    <>
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-soft p-6 mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Filter User Transactions</h3>
    
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
            {success}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">User</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              value={filters.userId || ''}
              onChange={(e) => handleFilterChange('userId', e.target.value)}
            >
              <option value="">All Users</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} ({user.email})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
            <input
              type="number"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              value={filters.amount || ''}
              onChange={(e) => handleFilterChange('amount', e.target.value)}
              placeholder="e.g. 66.62"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              value={filters.category || ''}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              placeholder="Food, Gas, Shopping, etc."
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button 
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200" 
            onClick={clearFilters}
          >
            Clear Filters
          </button>
          <button
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => fetchUserTransactions()}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* User Transactions Table */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 sm:mb-0">User Transactions ({pagination.total} total)</h3>
          {!isViewer && (
            <button
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleBulkAutoMatch}
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Auto-Match All'}
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="text-gray-500">Loading user transactions...</div>
          </div>
        ) : userTransactions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No user transactions found.</p>
          </div>
        ) : (
          <div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {sortableTh('User', 'user')}
                    {sortableTh('Date', 'date')}
                    {sortableTh('Vendor', 'vendor')}
                    {sortableTh('Category', 'category')}
                    {sortableTh('Amount', 'amount', 'right')}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credit Card</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                    {sortableTh('Status', 'status')}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {userTransactions.map(transaction => (
                    <tr key={transaction.id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {transaction.user ? `${transaction.user.firstName} ${transaction.user.lastName}` : 'Unknown'}
                        </div>
                        {transaction.user && (
                          <div className="text-xs text-gray-500">
                            {transaction.user.email}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">{formatDate(transaction.transactionDate)}</td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900">{transaction.vendor}</div>
                      </td>
                      <td className="px-4 py-4">
                        {transaction.category ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {transaction.category}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">No Category</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right text-sm font-semibold text-gray-900">
                        {formatCurrency(transaction.amount, transaction.type)}
                      </td>
                      <td className="px-4 py-4">
                        {transaction.statementTransaction?.cardNumber ? (
                          <div className="text-sm text-gray-900">
                            ••••{transaction.statementTransaction.cardNumber}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {transaction.notes ? (
                          <div className="max-w-xs text-sm text-gray-900 truncate" title={transaction.notes}>
                            {transaction.notes}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">No notes</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          transaction.isMatched 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {transaction.isMatched ? 'Matched' : 'Unmatched'}
                        </span>
                        {transaction.statementTransaction && (
                          <div className="text-xs text-gray-500 mt-1">
                            Linked to statement
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-1">
                          {transaction.receiptImagePath && (
                            <button
                              className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors duration-200"
                              onClick={() => handleOpenReceipt(transaction)}
                            >
                              📄 View Receipt
                            </button>
                          )}
                          {!isViewer && (
                            <>
                              <button
                                className={`px-3 py-1 text-white text-xs font-medium rounded-lg transition-colors duration-200 ${
                                  transaction.isMatched && transaction.statementTransaction?.statementGroup?.processed
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700'
                                }`}
                                onClick={() => handleEditUserTransaction(transaction)}
                                disabled={transaction.isMatched && transaction.statementTransaction?.statementGroup?.processed}
                                title={
                                  transaction.isMatched && transaction.statementTransaction?.statementGroup?.processed
                                    ? `Cannot edit: matched to processed statement group "${transaction.statementTransaction.statementGroup.name}"`
                                    : 'Edit this transaction'
                                }
                              >
                                ✏️ Edit
                              </button>
                              {transaction.isMatched ? (
                                <button
                                  className={`px-3 py-1 text-white text-xs font-medium rounded-lg transition-colors duration-200 ${
                                    transaction.statementTransaction?.statementGroup?.processed
                                      ? 'bg-gray-400 cursor-not-allowed'
                                      : 'bg-yellow-600 hover:bg-yellow-700'
                                  }`}
                                  onClick={() => handleUnmatchTransaction(transaction.id)}
                                  disabled={transaction.statementTransaction?.statementGroup?.processed}
                                  title={
                                    transaction.statementTransaction?.statementGroup?.processed
                                      ? `Cannot unmatch: matched to processed statement group "${transaction.statementTransaction.statementGroup.name}"`
                                      : 'Unmatch this transaction'
                                  }
                                >
                                  Unmatch
                                </button>
                              ) : (
                                <button
                                  className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors duration-200"
                                  onClick={() => handleUserTransactionMatch(transaction.id)}
                                >
                                  Match
                                </button>
                              )}
                              <button
                                className={`px-3 py-1 text-white text-xs font-medium rounded-lg transition-colors duration-200 ${
                                  transaction.isMatched && transaction.statementTransaction?.statementGroup?.processed
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-red-600 hover:bg-red-700'
                                }`}
                                onClick={() => handleDeleteUserTransaction(transaction.id, `${transaction.vendor} - ${formatCurrency(transaction.amount)}`)}
                                disabled={transaction.isMatched && transaction.statementTransaction?.statementGroup?.processed}
                                title={
                                  transaction.isMatched && transaction.statementTransaction?.statementGroup?.processed
                                    ? `Cannot delete: matched to processed statement group "${transaction.statementTransaction.statementGroup.name}"`
                                    : 'Delete this transaction'
                                }
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-center px-6 py-4 bg-gray-50 border-t border-gray-200 space-x-4">
                <button 
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200" 
                  onClick={() => goToPage(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Previous
                </button>
                
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                
                <button 
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200" 
                  onClick={() => goToPage(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );

  const renderSuggestedMatches = () => (
    <>
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 sm:mb-0">
            Suggested Matches ({suggestedMatchesPagination.total} pending)
          </h3>
          <p className="text-sm text-gray-500">
            Near-date matches found within ±2 days of user transactions
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mx-6 mt-4 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 mx-6 mt-4 rounded-lg">
            {success}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : suggestedMatches.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg font-medium">No suggested matches</p>
            <p className="text-sm mt-1">All transactions are either matched or have no near-date candidates.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Transaction</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statement Transaction</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Date Diff</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Confidence</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {suggestedMatches.map(suggestion => (
                  <tr key={suggestion.id} className="hover:bg-amber-50 transition-colors duration-150">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {suggestion.userTransaction?.user?.firstName} {suggestion.userTransaction?.user?.lastName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDate(suggestion.userTransaction?.transactionDate)} &bull; {formatCurrency(suggestion.userTransaction?.amount)}
                      </div>
                      <div className="text-xs text-gray-600 mt-0.5">
                        {suggestion.userTransaction?.vendor || 'No vendor'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {suggestion.statementTransaction?.description}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDate(suggestion.statementTransaction?.transactionDate)} &bull; {formatCurrency(suggestion.statementTransaction?.amount)}
                      </div>
                      <div className="text-xs text-gray-600 mt-0.5">
                        Card: ••••{suggestion.statementTransaction?.cardNumber}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        Math.abs(suggestion.dateDifference) === 1
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {suggestion.dateDifference > 0 ? '+' : ''}{suggestion.dateDifference} day{Math.abs(suggestion.dateDifference) !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        suggestion.confidence === 'high'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}>
                        {suggestion.confidence === 'high' ? 'High' : 'Medium'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        {!isViewer && (
                          <>
                            <button
                              onClick={() => handleApproveSuggestion(suggestion.id)}
                              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors duration-200"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleDismissSuggestion(suggestion.id)}
                              className="px-3 py-1.5 bg-gray-400 hover:bg-gray-500 text-white text-xs font-medium rounded-lg transition-colors duration-200"
                            >
                              Dismiss
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {suggestedMatchesPagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-gray-200">
            <button
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              onClick={() => setSuggestedMatchesPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              disabled={suggestedMatchesPagination.page === 1}
            >
              Previous
            </button>

            <span className="text-sm text-gray-600">
              Page {suggestedMatchesPagination.page} of {suggestedMatchesPagination.totalPages}
            </span>

            <button
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              onClick={() => setSuggestedMatchesPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
              disabled={suggestedMatchesPagination.page === suggestedMatchesPagination.totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );

  const renderDeletionLogs = () => (
    <>
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 sm:mb-0">Deletion Log ({deletionLogsPagination.total} total)</h3>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deleted At</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Card</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deleted By</th>
                  {!isViewer && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {deletionLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {new Date(log.deletedAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">{log.userName || 'Unknown'}</div>
                      <div className="text-xs text-gray-500">{log.userEmail || 'N/A'}</div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {log.transactionDate}
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">{log.vendor}</div>
                      {log.category && (
                        <div className="text-xs text-gray-500">{log.category}</div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {formatCurrency(log.amount)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {log.creditCardInfo || 'N/A'}
                    </td>
                    <td className="px-4 py-4">
                      {log.isRestored ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Restored
                        </span>
                      ) : (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          log.isMatched
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {log.isMatched ? 'Was Matched' : 'Was Unmatched'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">
                        {log.deletedByUser ? `${log.deletedByUser.firstName} ${log.deletedByUser.lastName}` : 'Unknown'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {log.deletedByUser?.email || 'N/A'}
                      </div>
                    </td>
                    {!isViewer && (
                      <td className="px-4 py-4">
                        {log.isRestored ? (
                          <div className="text-xs text-gray-500">
                            Restored on {new Date(log.restoredAt).toLocaleDateString('en-US')}
                          </div>
                        ) : (
                          <button
                            onClick={() => handleRestoreTransaction(log.id)}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded transition-colors duration-200"
                          >
                            Restore
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {deletionLogsPagination.totalPages > 1 && (
          <div className="flex items-center justify-center px-6 py-4 bg-gray-50 border-t border-gray-200 space-x-4">
            <button
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              onClick={() => setDeletionLogsPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={deletionLogsPagination.page === 1}
            >
              Previous
            </button>
            <span className="text-sm text-gray-700">
              Page {deletionLogsPagination.page} of {deletionLogsPagination.totalPages}
            </span>
            <button
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              onClick={() => setDeletionLogsPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={deletionLogsPagination.page === deletionLogsPagination.totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div>
      {/* View Toggle */}
      <div className="bg-white rounded-xl shadow-soft mb-8 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8" aria-label="Tabs">
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeView === 'groups'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => {
                setActiveView('groups');
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              Statement Groups
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeView === 'transactions'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => {
                setActiveView('transactions');
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              All Transactions
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeView === 'user-transactions'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => {
                setActiveView('user-transactions');
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              User Transactions
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 relative ${
                activeView === 'suggested-matches'
                  ? 'border-amber-500 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => {
                setActiveView('suggested-matches');
                setSuggestedMatchesPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              Suggested Matches
              {suggestedMatchCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-amber-500 text-white">
                  {suggestedMatchCount}
                </span>
              )}
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeView === 'deletion-logs'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => {
                setActiveView('deletion-logs');
                setDeletionLogsPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              Deletion Log
            </button>
          </nav>
        </div>
      </div>

      {activeView === 'groups' ? renderStatementGroups() : activeView === 'user-transactions' ? renderUserTransactions() : activeView === 'suggested-matches' ? renderSuggestedMatches() : activeView === 'deletion-logs' ? renderDeletionLogs() : (
        <>
          {/* Filters */}
          <div className="bg-white rounded-xl shadow-soft p-6 mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Filter Transactions</h3>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
            {success}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Statement Group</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              value={filters.statementGroupId}
              onChange={(e) => handleFilterChange('statementGroupId', e.target.value)}
            >
              <option value="">All Groups</option>
              {statementGroups.map(group => (
                <option key={group.id} value={group.id}>
                  {group.name} ({formatStatementMonth(group.statementMonth)})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Vendor</label>
            <SearchableSelect
              value={filters.vendorId}
              onChange={(value) => handleFilterChange('vendorId', value)}
              options={[
                { id: '', label: 'All Vendors' },
                { id: 'null', label: 'No Vendor' },
                ...vendors.map(v => ({ id: v.id, label: v.name }))
              ]}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.id}
              placeholder="All Vendors"
              searchPlaceholder="Search vendors..."
              allowEmpty={false}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">GL Account</label>
            <SearchableSelect
              value={filters.glAccountId}
              onChange={(value) => handleFilterChange('glAccountId', value)}
              options={[
                { id: '', label: 'All GL Accounts' },
                { id: 'null', label: 'Missing GL' },
                ...glAccounts.map(a => ({ id: a.id, label: `${a.accountNumber} - ${a.accountName}` }))
              ]}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.id}
              placeholder="All GL Accounts"
              searchPlaceholder="Search GL accounts..."
              allowEmpty={false}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
            <SearchableSelect
              value={filters.locationId}
              onChange={(value) => handleFilterChange('locationId', value)}
              options={[
                { id: '', label: 'All Locations' },
                { id: 'null', label: 'Missing Location' },
                ...locations.map(l => ({ id: l.id, label: `${l.locationNumber} - ${l.locationName}` }))
              ]}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.id}
              placeholder="All Locations"
              searchPlaceholder="Search locations..."
              allowEmpty={false}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Match Status</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              value={filters.isMatched}
              onChange={(e) => handleFilterChange('isMatched', e.target.value)}
            >
              <option value="">All Transactions</option>
              <option value="true">Matched Only</option>
              <option value="false">Unmatched Only</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Card Number</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              value={filters.cardNumber}
              onChange={(e) => handleFilterChange('cardNumber', e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="1234"
              maxLength="4"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="includeArchivedTransactions"
              checked={filters.includeArchived || false}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, includeArchived: e.target.checked }));
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
            />
            <label htmlFor="includeArchivedTransactions" className="text-sm text-gray-700 cursor-pointer">
              Include Archived
            </label>
          </div>
          <button
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200"
            onClick={clearFilters}
          >
            Clear Filters
          </button>
          <button
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => activeView === 'user-transactions' ? fetchUserTransactions() : fetchTransactions()}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          {activeView === 'transactions' && !isViewer && (
            <button
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
              onClick={handleExportR365}
              disabled={loading}
            >
              <Download className="w-4 h-4 mr-2" />
              Export R365 Upload
            </button>
          )}
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 sm:mb-0">Transactions ({pagination.total} total)</h3>
          {!isViewer && selectedTransactions.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                {selectedTransactions.length} selected
              </span>
              <button
                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg transition-colors duration-200"
                onClick={handleBulkDelete}
              >
                Delete Selected
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="text-gray-500">Loading transactions...</div>
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No transactions found. Upload a CSV file to get started.</p>
          </div>
        ) : (
          <div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {!isViewer && (
                      <th className="w-10 px-2 py-2 text-left">
                        <input
                          type="checkbox"
                          checked={selectedTransactions.length === transactions.length && transactions.length > 0}
                          onChange={handleSelectAll}
                          className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
                        />
                      </th>
                    )}
                    {sortableTh('Card', 'card', 'left', 'px-2 py-2')}
                    {sortableTh('Post Date', 'date', 'left', 'px-2 py-2')}
                    {sortableTh('Description', 'description', 'left', 'px-2 py-2')}
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    {sortableTh('Amount', 'amount', 'right', 'px-2 py-2')}
                    {sortableTh('Vendor', 'vendor', 'left', 'px-2 py-2')}
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GL Account</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt</th>
                    {sortableTh('Status', 'status', 'left', 'px-2 py-2')}
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map(transaction => (
                    <tr key={transaction.id} className="hover:bg-gray-50 transition-colors duration-150">
                      {!isViewer && (
                        <td className="px-2 py-3">
                          <input
                            type="checkbox"
                            checked={selectedTransactions.includes(transaction.id)}
                            onChange={() => handleSelectTransaction(transaction.id)}
                            className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
                          />
                        </td>
                      )}
                      <td className="px-2 py-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {transaction.cardNumber}
                        </span>
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-900">{formatDate(transaction.transactionDate)}</td>
                      <td className="px-2 py-3">
                        <div className="max-w-xs text-sm text-gray-900 truncate" title={transaction.description}>
                          {transaction.description}
                        </div>
                      </td>
                      <td className="px-2 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                          {transaction.type || 'N/A'}
                        </span>
                      </td>
                      <td className="px-2 py-3 text-right text-sm font-semibold text-gray-900">
                        {formatCurrency(transaction.amount, transaction.type)}
                      </td>
                      <td className="px-2 py-3">
                        {isViewer ? (
                          <div className="text-sm text-gray-900">
                            {transaction.vendor?.name || <span className="text-gray-400 italic">No Vendor</span>}
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            <SearchableSelect
                              value={transaction.vendorId || ''}
                              onChange={(value) => handleVendorChange(transaction.id, value)}
                              options={vendors}
                              getOptionLabel={(vendor) => vendor.name}
                              getOptionValue={(vendor) => vendor.id}
                              placeholder="No Vendor"
                              searchPlaceholder="Search vendors..."
                              emptyLabel="No Vendor"
                              className="min-w-[120px]"
                            />
                            {!transaction.vendorId && (
                              <button
                                onClick={() => handleOpenVendorSearchModal(transaction)}
                                className="text-xs text-blue-600 hover:text-blue-800 underline text-left"
                              >
                                Search Vendors
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-3">
                        {isViewer ? (
                          <div className="text-sm text-gray-900">
                            {(() => {
                              const glAccount = transaction.glAccount || transaction.vendor?.glAccount;
                              if (glAccount) {
                                return (
                                  <div>
                                    <div>{glAccount.accountNumber} - {glAccount.accountName}</div>
                                    {!transaction.glAccountId && transaction.vendor?.glAccount && (
                                      <div className="text-xs text-gray-500 mt-1">Default from vendor</div>
                                    )}
                                  </div>
                                );
                              }
                              return <span className="text-gray-400 italic">No GL Account</span>;
                            })()}
                          </div>
                        ) : (
                          <SearchableGLAccountSelect
                            value={transaction.glAccountId || (transaction.vendor?.glAccountId || '')}
                            onChange={(glAccountId) => handleGLAccountChange(transaction.id, glAccountId)}
                            glAccounts={glAccounts}
                            defaultLabel={
                              !transaction.glAccountId && transaction.vendor?.glAccount
                                ? `Default: ${transaction.vendor.glAccount.accountNumber}`
                                : null
                            }
                          />
                        )}
                      </td>
                      <td className="px-2 py-3">
                        {isViewer ? (
                          <div className="text-sm text-gray-900">
                            {(() => {
                              const location = transaction.location ||
                                             transaction.userTransaction?.creditCard?.location ||
                                             transaction.userTransaction?.user?.creditCard?.location;
                              if (location) {
                                return (
                                  <div>
                                    <div>{location.locationNumber} - {location.locationName}</div>
                                    {!transaction.locationId && (transaction.userTransaction?.creditCard?.location || transaction.userTransaction?.user?.creditCard?.location) && (
                                      <div className="text-xs text-gray-500 mt-1">Default from credit card</div>
                                    )}
                                  </div>
                                );
                              }
                              return <span className="text-gray-400 italic">No Location</span>;
                            })()}
                          </div>
                        ) : (
                          <SearchableSelect
                            value={transaction.locationId || (transaction.userTransaction?.creditCard?.locationId || transaction.userTransaction?.user?.creditCard?.locationId || '')}
                            onChange={(locationId) => handleLocationChange(transaction.id, locationId)}
                            options={locations}
                            getOptionLabel={(location) => `${location.locationNumber} - ${location.locationName}`}
                            getOptionValue={(location) => location.id}
                            placeholder="No Location"
                            searchPlaceholder="Search locations..."
                            emptyLabel="No Location"
                            className="min-w-[150px]"
                          />
                        )}
                      </td>
                      <td className="px-2 py-3">
                        {transaction.userTransaction && transaction.userTransaction.receiptImagePath ? (
                          <button
                            className="inline-flex items-center px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors duration-200"
                            onClick={() => handleOpenReceipt(transaction.userTransaction)}
                          >
                            📄 View Receipt
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">No Receipt</span>
                        )}
                      </td>
                      <td className="px-2 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          transaction.isMatched
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {transaction.isMatched ? 'Matched' : 'Unmatched'}
                        </span>
                      </td>
                      <td className="px-2 py-3">
                        {!isViewer && (
                          <div className="flex flex-col gap-1">
                            {transaction.isMatched ? (
                              <button
                                className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-xs font-medium rounded-lg transition-colors duration-200"
                                onClick={() => handleStatementTransactionUnmatch(transaction.id)}
                              >
                                Unmatch
                              </button>
                            ) : (
                              <button
                                className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors duration-200"
                                onClick={() => handleStatementTransactionMatch(transaction.id)}
                              >
                                Match
                              </button>
                            )}
                            <button
                              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg transition-colors duration-200"
                              onClick={() => handleDeleteTransaction(transaction.id, transaction.description)}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-center px-6 py-4 bg-gray-50 border-t border-gray-200 space-x-4">
                <button 
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200" 
                  onClick={() => goToPage(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Previous
                </button>
                
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                
                <button 
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200" 
                  onClick={() => goToPage(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>
        </>
      )}

      {/* Matching Modal */}
      {showMatchModal && selectedStatementTransaction && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowMatchModal(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" id="modal-title">
                      Match Statement Transaction
                    </h3>
                    
                    {/* Statement Transaction Info */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Statement Transaction Details:</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium">Date:</span> {formatDate(selectedStatementTransaction.transactionDate)}
                        </div>
                        <div>
                          <span className="font-medium">Amount:</span> {formatCurrency(selectedStatementTransaction.amount)}
                        </div>
                        <div className="col-span-2">
                          <span className="font-medium">Description:</span> {selectedStatementTransaction.description}
                        </div>
                        <div>
                          <span className="font-medium">Card:</span> •••{selectedStatementTransaction.cardNumber}
                        </div>
                      </div>
                    </div>

                    {/* Suggested Matches (Near-Date) */}
                    {modalSuggestedMatches.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-amber-800 mb-2">Suggested Matches (Near-Date):</h4>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
                          <table className="w-full">
                            <thead className="bg-amber-100">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-amber-800 uppercase">User</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-amber-800 uppercase">Date</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-amber-800 uppercase">Vendor</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-amber-800 uppercase">Amount</th>
                                <th className="px-3 py-2 text-center text-xs font-medium text-amber-800 uppercase">Date Diff</th>
                                <th className="px-3 py-2 text-center text-xs font-medium text-amber-800 uppercase">Action</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-amber-200">
                              {modalSuggestedMatches.map(suggestion => (
                                <tr key={suggestion.id} className="hover:bg-amber-100 transition-colors">
                                  <td className="px-3 py-2">
                                    <div className="text-sm font-medium text-gray-900">
                                      {suggestion.userTransaction?.user?.firstName} {suggestion.userTransaction?.user?.lastName}
                                    </div>
                                    <div className="text-xs text-gray-500">{suggestion.userTransaction?.user?.email}</div>
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-900">{formatDate(suggestion.userTransaction?.transactionDate)}</td>
                                  <td className="px-3 py-2 text-sm text-gray-900">{suggestion.userTransaction?.vendor || '-'}</td>
                                  <td className="px-3 py-2 text-right text-sm font-medium text-gray-900">
                                    {formatCurrency(suggestion.userTransaction?.amount)}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                      suggestion.confidence === 'high' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                                    }`}>
                                      {suggestion.dateDifference > 0 ? '+' : ''}{suggestion.dateDifference}d
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <div className="flex items-center justify-center space-x-1">
                                      <button
                                        className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded transition-colors duration-200"
                                        onClick={() => handleConfirmMatch(suggestion.userTransaction?.id)}
                                      >
                                        Match
                                      </button>
                                      <button
                                        className="px-2 py-1 bg-gray-400 hover:bg-gray-500 text-white text-xs font-medium rounded transition-colors duration-200"
                                        onClick={() => handleDismissSuggestion(suggestion.id)}
                                      >
                                        Dismiss
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Available User Transactions */}
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-medium text-gray-900">Available User Transactions (within 10 days):</h4>
                        <button
                          onClick={handleOpenCreateTransaction}
                          className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors duration-200"
                        >
                          + Create New Transaction
                        </button>
                      </div>
                      {availableUserTransactions.length === 0 ? (
                        <div className="text-gray-500 text-center py-8">
                          <p>No unmatched user transactions available.</p>
                          <p className="text-sm mt-2">Use the "Create New Transaction" button above to add one.</p>
                        </div>
                      ) : (
                        <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                          <table className="w-full">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                {(() => {
                                  const stmtDateMs = selectedStatementTransaction?.transactionDate
                                    ? new Date(selectedStatementTransaction.transactionDate).getTime()
                                    : null;
                                  const stmtAmount = selectedStatementTransaction?.amount != null
                                    ? Math.abs(parseFloat(selectedStatementTransaction.amount))
                                    : null;
                                  const toggleSort = (by) => {
                                    setMatchModalSort(prev => prev.by === by
                                      ? { by, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                                      : { by, dir: by === 'amount' || by === 'date' ? 'desc' : 'asc' });
                                  };
                                  const arrow = (by) => matchModalSort.by === by
                                    ? (matchModalSort.dir === 'asc' ? ' ▲' : ' ▼')
                                    : '';
                                  const headerBtn = (label, by, align = 'left') => (
                                    <button
                                      type="button"
                                      onClick={() => toggleSort(by)}
                                      className={`w-full text-${align} text-xs font-medium text-gray-500 uppercase hover:text-gray-700`}
                                    >
                                      {label}{arrow(by)}
                                    </button>
                                  );
                                  return (
                                    <>
                                      <th className="px-3 py-2 text-left">{headerBtn('User', 'user')}</th>
                                      <th className="px-3 py-2 text-left">
                                        <div className="flex gap-3 items-center">
                                          {headerBtn('Date', 'date')}
                                          {stmtDateMs && (
                                            <button
                                              type="button"
                                              onClick={() => toggleSort('closestDate')}
                                              className="text-[10px] font-medium text-blue-600 hover:text-blue-800 normal-case"
                                              title="Sort by distance from statement date"
                                            >
                                              Closest{arrow('closestDate')}
                                            </button>
                                          )}
                                        </div>
                                      </th>
                                      <th className="px-3 py-2 text-left">{headerBtn('Vendor', 'vendor')}</th>
                                      <th className="px-3 py-2 text-right">
                                        <div className="flex gap-3 items-center justify-end">
                                          {headerBtn('Amount', 'amount', 'right')}
                                          {stmtAmount != null && (
                                            <button
                                              type="button"
                                              onClick={() => toggleSort('closestAmount')}
                                              className="text-[10px] font-medium text-blue-600 hover:text-blue-800 normal-case"
                                              title="Sort by amount closest to statement"
                                            >
                                              Closest{arrow('closestAmount')}
                                            </button>
                                          )}
                                        </div>
                                      </th>
                                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Receipt</th>
                                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                                    </>
                                  );
                                })()}
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {(() => {
                                const stmtDateMs = selectedStatementTransaction?.transactionDate
                                  ? new Date(selectedStatementTransaction.transactionDate).getTime()
                                  : null;
                                const stmtAmount = selectedStatementTransaction?.amount != null
                                  ? Math.abs(parseFloat(selectedStatementTransaction.amount))
                                  : null;
                                const cmpKey = (t) => {
                                  switch (matchModalSort.by) {
                                    case 'user':
                                      return `${t.user?.lastName || ''} ${t.user?.firstName || ''}`.toLowerCase();
                                    case 'vendor':
                                      return (t.vendor || '').toLowerCase();
                                    case 'amount':
                                      return Math.abs(parseFloat(t.amount) || 0);
                                    case 'closestAmount':
                                      return stmtAmount != null
                                        ? Math.abs(Math.abs(parseFloat(t.amount) || 0) - stmtAmount)
                                        : 0;
                                    case 'closestDate':
                                      return stmtDateMs != null
                                        ? Math.abs(new Date(t.transactionDate).getTime() - stmtDateMs)
                                        : 0;
                                    case 'date':
                                    default:
                                      return new Date(t.transactionDate).getTime();
                                  }
                                };
                                const sorted = [...availableUserTransactions].sort((a, b) => {
                                  const ka = cmpKey(a);
                                  const kb = cmpKey(b);
                                  if (ka < kb) return matchModalSort.dir === 'asc' ? -1 : 1;
                                  if (ka > kb) return matchModalSort.dir === 'asc' ? 1 : -1;
                                  return 0;
                                });
                                return sorted.map(userTxn => (
                                <tr key={userTxn.id} className="hover:bg-gray-50">
                                  <td className="px-3 py-2">
                                    <div className="text-sm font-medium text-gray-900">
                                      {userTxn.user.firstName} {userTxn.user.lastName}
                                    </div>
                                    <div className="text-xs text-gray-500">{userTxn.user.email}</div>
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-900">{formatDate(userTxn.transactionDate)}</td>
                                  <td className="px-3 py-2">
                                    <div className="text-sm text-gray-900">{userTxn.vendor}</div>
                                    {userTxn.category && (
                                      <div className="text-xs text-gray-500">{userTxn.category}</div>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-right text-sm font-medium text-gray-900">
                                    {formatCurrency(userTxn.amount, userTxn.type)}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {userTxn.receiptImagePath ? (
                                      <button
                                        className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded transition-colors duration-200"
                                        onClick={() => handleOpenReceipt(userTxn)}
                                      >
                                        📄 View
                                      </button>
                                    ) : (
                                      <span className="text-xs text-gray-400">-</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <button
                                      className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors duration-200"
                                      onClick={() => handleConfirmMatch(userTxn.id)}
                                    >
                                      Match
                                    </button>
                                  </td>
                                </tr>
                                ));
                              })()}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowMatchModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Transaction Match Modal - Show Available Statement Transactions */}
      {showUserMatchModal && selectedUserTransactionForMatch && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="user-match-modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowUserMatchModal(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" id="user-match-modal-title">
                      Match User Transaction to Statement
                    </h3>

                    {/* User Transaction Info */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">User Transaction Details:</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium">Date:</span> {formatDate(selectedUserTransactionForMatch.transactionDate)}
                        </div>
                        <div>
                          <span className="font-medium">Amount:</span> {formatCurrency(selectedUserTransactionForMatch.amount)}
                        </div>
                        <div className="col-span-2">
                          <span className="font-medium">Vendor:</span> {selectedUserTransactionForMatch.vendor}
                        </div>
                        {selectedUserTransactionForMatch.category && (
                          <div>
                            <span className="font-medium">Category:</span> {selectedUserTransactionForMatch.category}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Suggested Statement Matches (Near-Date) */}
                    {modalSuggestedStmtMatches.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-amber-800 mb-2">Suggested Matches (Near-Date):</h4>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
                          <table className="w-full">
                            <thead className="bg-amber-100">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-amber-800 uppercase">Date</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-amber-800 uppercase">Description</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-amber-800 uppercase">Amount</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-amber-800 uppercase">Card</th>
                                <th className="px-3 py-2 text-center text-xs font-medium text-amber-800 uppercase">Date Diff</th>
                                <th className="px-3 py-2 text-center text-xs font-medium text-amber-800 uppercase">Action</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-amber-200">
                              {modalSuggestedStmtMatches.map(suggestion => (
                                <tr key={suggestion.id} className="hover:bg-amber-100 transition-colors">
                                  <td className="px-3 py-2 text-sm text-gray-900">{formatDate(suggestion.statementTransaction?.transactionDate)}</td>
                                  <td className="px-3 py-2">
                                    <div className="text-sm text-gray-900">{suggestion.statementTransaction?.description}</div>
                                  </td>
                                  <td className="px-3 py-2 text-right text-sm font-medium text-gray-900">
                                    {formatCurrency(suggestion.statementTransaction?.amount)}
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className="text-xs text-gray-500">••••{suggestion.statementTransaction?.cardNumber}</div>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                      suggestion.confidence === 'high' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                                    }`}>
                                      {suggestion.dateDifference > 0 ? '+' : ''}{suggestion.dateDifference}d
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <div className="flex items-center justify-center space-x-1">
                                      <button
                                        className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded transition-colors duration-200"
                                        onClick={() => handleConfirmUserMatch(suggestion.statementTransaction?.id)}
                                      >
                                        Match
                                      </button>
                                      <button
                                        className="px-2 py-1 bg-gray-400 hover:bg-gray-500 text-white text-xs font-medium rounded transition-colors duration-200"
                                        onClick={() => handleDismissSuggestion(suggestion.id)}
                                      >
                                        Dismiss
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Available Statement Transactions */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Available Statement Transactions:</h4>
                      {availableStatementTransactions.length === 0 ? (
                        <div className="text-gray-500 text-center py-8">
                          <p>No unmatched statement transactions found.</p>
                          <p className="text-sm mt-2">Make sure statement transactions have been uploaded for this user's credit cards.</p>
                        </div>
                      ) : (
                        <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                          <table className="w-full">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Card</th>
                                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {availableStatementTransactions.map(stmt => (
                                <tr key={stmt.id} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 text-sm text-gray-900">{formatDate(stmt.transactionDate)}</td>
                                  <td className="px-3 py-2">
                                    <div className="text-sm text-gray-900">{stmt.description}</div>
                                    {stmt.category && (
                                      <div className="text-xs text-gray-500">{stmt.category}</div>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-right text-sm font-medium text-gray-900">
                                    {formatCurrency(stmt.amount, stmt.type)}
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className="text-xs text-gray-500">••••{stmt.cardNumber}</div>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <button
                                      className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors duration-200"
                                      onClick={() => handleConfirmUserMatch(stmt.id)}
                                    >
                                      Match
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowUserMatchModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Lightbox Modal */}
      {showReceiptModal && selectedUserTransaction && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="receipt-modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={handleCloseReceipt}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="receipt-modal-title">
                    {selectedUserTransaction.receiptImagePath.toLowerCase().endsWith('.pdf') ? 'Receipt PDF' : 'Receipt Image'}
                  </h3>
                  <div className="flex items-center gap-2">
                    <a
                      href={`/api/user-transactions/receipt-image/${selectedUserTransaction.receiptImagePath.split('/').pop()}?download`}
                      download={selectedUserTransaction.receiptImagePath.split('/').pop()}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-md transition-colors"
                      title="Download receipt"
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </a>
                    <button
                      type="button"
                      className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      onClick={handleCloseReceipt}
                    >
                      <span className="sr-only">Close</span>
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="flex justify-center mb-6">
                  {selectedUserTransaction.receiptImagePath.toLowerCase().endsWith('.pdf') ? (
                    <iframe
                      src={`/api/user-transactions/receipt-image/${selectedUserTransaction.receiptImagePath.split('/').pop()}`}
                      title="Receipt PDF"
                      className="w-full h-[70vh] rounded-lg shadow-lg"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : (
                    <ImageZoom
                      src={`/api/user-transactions/receipt-image/${selectedUserTransaction.receiptImagePath.split('/').pop()}`}
                      alt="Receipt"
                      className="w-full min-h-[70vh] flex items-center justify-center rounded-lg shadow-lg"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.nextSibling.style.display = 'block';
                      }}
                    />
                  )}
                  <div style={{display: 'none'}} className="text-center py-8">
                    <div className="text-gray-500">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="mt-2">Unable to load receipt</p>
                    </div>
                  </div>
                </div>
                
                {/* Transaction Details Panel - Always visible */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <h4 className="text-sm font-medium text-gray-900">Transaction Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Date:</span>
                      <span className="ml-2 text-gray-900">
                        {selectedUserTransaction.transactionDate
                          ? formatDate(selectedUserTransaction.transactionDate)
                          : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Amount:</span>
                      <span className="ml-2 text-gray-900">
                        {selectedUserTransaction.amount 
                          ? `$${parseFloat(selectedUserTransaction.amount).toFixed(2)}` 
                          : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Vendor:</span>
                      <span className="ml-2 text-gray-900">
                        {selectedUserTransaction.vendor || '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Category:</span>
                      <span className="ml-2 text-gray-900">
                        {selectedUserTransaction.category || '-'}
                      </span>
                    </div>
                  </div>
                  {selectedUserTransaction.notes && (
                    <div>
                      <span className="text-gray-500">Notes:</span>
                      <p className="mt-1 text-gray-900">{selectedUserTransaction.notes}</p>
                    </div>
                  )}
                  <div className="text-xs text-gray-400">
                    File: {selectedUserTransaction.receiptImagePath}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create User Transaction Modal */}
      {showCreateTransactionModal && selectedStatementTransaction && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="create-modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={handleCloseCreateTransaction}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleCreateTransactionSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" id="create-modal-title">
                        Create User Transaction
                      </h3>
                      
                      {/* Statement Transaction Reference */}
                      <div className="bg-gray-50 rounded-lg p-3 mb-6 text-sm">
                        <h4 className="font-medium text-gray-900 mb-1">Matching to Statement Transaction:</h4>
                        <p>{selectedStatementTransaction.description} - ${selectedStatementTransaction.amount}</p>
                        <p className="text-gray-600">Card ending {selectedStatementTransaction.cardNumber}</p>
                      </div>

                      {/* In-modal error banner — the page-level error banner renders behind this
                          z-50 modal, so failures (e.g. no user assigned to the card) were invisible. */}
                      {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                          {error}
                        </div>
                      )}

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Transaction Date</label>
                          <input
                            type="date"
                            value={createTransactionForm.transactionDate}
                            onChange={(e) => handleCreateTransactionFormChange('transactionDate', e.target.value)}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Amount</label>
                          <input
                            type="number"
                            step="0.01"
                            value={createTransactionForm.amount}
                            onChange={(e) => handleCreateTransactionFormChange('amount', e.target.value)}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Vendor</label>
                          <input
                            type="text"
                            value={createTransactionForm.vendor}
                            onChange={(e) => handleCreateTransactionFormChange('vendor', e.target.value)}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            required
                          />
                        </div>


                        <div>
                          <label className="block text-sm font-medium text-gray-700">Notes (Optional)</label>
                          <textarea
                            value={createTransactionForm.notes}
                            onChange={(e) => handleCreateTransactionFormChange('notes', e.target.value)}
                            rows={2}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Receipt Image *</label>
                          <input
                            type="file"
                            onChange={handleFileSelect}
                            accept="image/*,.pdf"
                            required
                            className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          />
                          {createTransactionForm.receiptImage && (
                            <p className="mt-1 text-sm text-gray-600">Selected: {createTransactionForm.receiptImage.name}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={isCreatingTransaction}
                    className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-blue-500 text-base font-medium text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {isCreatingTransaction ? 'Creating...' : 'Create & Match'}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={handleCloseCreateTransaction}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Fixed positioned dropdown menu */}
      {openDropdownId && openDropdownGroup && (
        <div
          className="fixed w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200 dropdown-menu"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`
          }}
        >
          <div className="py-1">
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
              onClick={() => {
                console.log('Edit button clicked, group:', openDropdownGroup);
                handleEditStatementGroup(openDropdownGroup);
              }}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Group
            </button>
            <button
              className={`w-full text-left px-4 py-2 text-sm flex items-center ${
                openDropdownGroup.processed
                  ? 'text-orange-600 hover:bg-orange-50'
                  : 'text-green-600 hover:bg-green-50'
              }`}
              onClick={() => {
                handleToggleProcessedStatus(openDropdownGroup.id, openDropdownGroup.name, openDropdownGroup.processed);
              }}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {openDropdownGroup.processed ? 'Unmark as Processed' : 'Mark as Processed'}
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-yellow-600 hover:bg-yellow-50 flex items-center"
              onClick={() => {
                setOpenDropdownId(null);
                setOpenDropdownGroup(null);
                handleArchiveStatementGroup(openDropdownGroup.id, openDropdownGroup.name, !openDropdownGroup.isActive);
              }}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
              {openDropdownGroup.isActive ? 'Archive Group' : 'Unarchive Group'}
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
              onClick={() => {
                console.log('Delete button clicked, group:', openDropdownGroup);
                setOpenDropdownId(null);
                setOpenDropdownGroup(null);
                handleDeleteStatementGroup(openDropdownGroup.id, openDropdownGroup.name, openDropdownGroup.totalTransactions);
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Group
            </button>
          </div>
        </div>
      )}

      {/* Edit Statement Group Modal */}
      {showEditStatementGroupModal && editingStatementGroup && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="edit-group-modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={handleCloseEditStatementGroup}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleEditStatementGroupSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" id="edit-group-modal-title">
                        Edit Statement Group
                      </h3>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Group Name</label>
                          <input
                            type="text"
                            value={editStatementGroupForm.name}
                            onChange={(e) => handleEditStatementGroupFormChange('name', e.target.value)}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Notes (Optional)</label>
                          <textarea
                            value={editStatementGroupForm.notes}
                            onChange={(e) => handleEditStatementGroupFormChange('notes', e.target.value)}
                            rows={3}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Add any notes about this statement group..."
                          />
                        </div>

                        <div className="bg-gray-50 rounded-lg p-3 text-sm">
                          <h4 className="font-medium text-gray-900 mb-1">Statement Group Info:</h4>
                          <p><span className="font-medium">Statement Month:</span> {formatStatementMonth(editingStatementGroup.statementMonth)}</p>
                          <p><span className="font-medium">Total Transactions:</span> {editingStatementGroup.totalTransactions}</p>
                          <p><span className="font-medium">Total Amount:</span> {formatCurrency(editingStatementGroup.totalAmount)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-blue-500 text-base font-medium text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={handleCloseEditStatementGroup}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Transaction Modal */}
      {showEditUserTransactionModal && editingUserTransaction && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowEditUserTransactionModal(false)}></div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleUpdateUserTransaction}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="w-full mt-3 text-center sm:mt-0 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        Edit User Transaction
                      </h3>

                      {/* Transaction Info */}
                      <div className="mb-4 p-3 bg-gray-50 rounded-md">
                        <div className="text-sm text-gray-700">
                          <div><strong>User:</strong> {editingUserTransaction.user?.firstName} {editingUserTransaction.user?.lastName}</div>
                        </div>
                      </div>

                      {/* Vendor */}
                      <div className="mb-4">
                        <label htmlFor="vendor" className="block text-sm font-medium text-gray-700 mb-2">
                          Vendor
                        </label>
                        <input
                          type="text"
                          id="vendor"
                          maxLength="255"
                          value={editUserTransactionForm.vendor}
                          onChange={(e) => setEditUserTransactionForm({ ...editUserTransactionForm, vendor: e.target.value })}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md border"
                          required
                        />
                      </div>

                      {/* Transaction Date */}
                      <div className="mb-4">
                        <label htmlFor="transactionDate" className="block text-sm font-medium text-gray-700 mb-2">
                          Transaction Date
                        </label>
                        <input
                          type="date"
                          id="transactionDate"
                          value={editUserTransactionForm.transactionDate}
                          onChange={(e) => setEditUserTransactionForm({ ...editUserTransactionForm, transactionDate: e.target.value })}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md border"
                          required
                        />
                      </div>

                      {/* Amount */}
                      <div className="mb-4">
                        <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-2">
                          Amount
                        </label>
                        <input
                          type="number"
                          id="amount"
                          step="0.01"
                          value={editUserTransactionForm.amount}
                          onChange={(e) => setEditUserTransactionForm({ ...editUserTransactionForm, amount: e.target.value })}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md border"
                          required
                        />
                      </div>

                      {/* Notes */}
                      <div className="mb-4">
                        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                          Notes
                        </label>
                        <textarea
                          id="notes"
                          rows="3"
                          value={editUserTransactionForm.notes}
                          onChange={(e) => setEditUserTransactionForm({ ...editUserTransactionForm, notes: e.target.value })}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md border"
                          placeholder="Add any additional notes..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => setShowEditUserTransactionModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Vendor Search Modal */}
      {showVendorSearchModal && selectedTransactionForVendor && (
        <VendorSearchModal
          transaction={selectedTransactionForVendor}
          onClose={() => {
            setShowVendorSearchModal(false);
            setSelectedTransactionForVendor(null);
          }}
          onVendorSelected={handleVendorSelected}
        />
      )}
    </div>
  );
};

export default TransactionManagement;